/**
 * Cross-subdomain consent storage for `*.getsquire.com`.
 *
 * Why a cookie: localStorage is origin-scoped, so consent set on
 * `getsquire.com/discover` (locale-pages) is invisible to
 * `squire-mobile-flagship-prod-1.getsquire.com`. A cookie scoped to
 * `.getsquire.com` is readable by both. See SQR-9273.
 *
 * Reads prefer the cookie, fall back to legacy localStorage, and backfill
 * the cookie when only localStorage is set so existing users skip a
 * second prompt. Writes set both during the migration window.
 *
 * Inside the widget iframe the frame's storage is partitioned per host, so
 * consent lives in memory and is loaded from / written to the host page
 * through the frame loader's broker (SQR-13811).
 */
import { isWidget } from '@/utils/isWidget.web';
import {
  pushConsentToParent,
  requestParentConsent,
} from '@/utils/widgetMessaging.web';

import type { CookieConsentState } from './cookieConsentStorage';

const STORAGE_KEY = 'squire_cookie_consent';
const COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 365;
const SHARED_DOMAIN_SUFFIX = 'getsquire.com';

export const CONSENT_VERSION = 1;

let widgetConsent: CookieConsentState | null = null;
let widgetLoad: Promise<CookieConsentState | null> | null = null;

const hasWindow = (): boolean => typeof window !== 'undefined';
const hasDocument = (): boolean => typeof document !== 'undefined';

const getCookieDomain = (): string | null => {
  if (!hasWindow()) return null;
  const host = window.location.hostname;
  if (
    host === SHARED_DOMAIN_SUFFIX ||
    host.endsWith(`.${SHARED_DOMAIN_SUFFIX}`)
  ) {
    return `.${SHARED_DOMAIN_SUFFIX}`;
  }
  return null;
};

const isSecureContext = (): boolean =>
  hasWindow() && window.location.protocol === 'https:';

const parseConsent = (raw: string | null): CookieConsentState | null => {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (
      parsed &&
      typeof parsed === 'object' &&
      typeof parsed.necessary === 'boolean' &&
      typeof parsed.statistics === 'boolean' &&
      typeof parsed.version === 'number' &&
      typeof parsed.date === 'string'
    ) {
      return parsed as CookieConsentState;
    }
  } catch {
    // fall through
  }
  return null;
};

const readCookie = (): string | null => {
  if (!hasDocument() || !document.cookie) return null;
  const prefix = `${STORAGE_KEY}=`;
  for (const part of document.cookie.split('; ')) {
    if (part.startsWith(prefix)) {
      try {
        return decodeURIComponent(part.slice(prefix.length));
      } catch {
        return null;
      }
    }
  }
  return null;
};

const writeCookie = (value: string): void => {
  if (!hasDocument()) return;
  const segments = [
    `${STORAGE_KEY}=${encodeURIComponent(value)}`,
    'Path=/',
    'SameSite=Lax',
    `Max-Age=${COOKIE_MAX_AGE_SECONDS}`,
  ];
  const domain = getCookieDomain();
  if (domain) segments.push(`Domain=${domain}`);
  if (isSecureContext()) segments.push('Secure');
  document.cookie = segments.join('; ');
};

const deleteCookie = (): void => {
  if (!hasDocument()) return;
  const segments = [`${STORAGE_KEY}=`, 'Path=/', 'Max-Age=0'];
  const domain = getCookieDomain();
  if (domain) segments.push(`Domain=${domain}`);
  document.cookie = segments.join('; ');
};

const readLocalStorage = (): string | null => {
  if (!hasWindow() || !window.localStorage) return null;
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
};

const writeLocalStorage = (value: string): void => {
  if (!hasWindow() || !window.localStorage) return;
  try {
    window.localStorage.setItem(STORAGE_KEY, value);
  } catch {
    // storage full / disabled / private mode
  }
};

const removeLocalStorage = (): void => {
  if (!hasWindow() || !window.localStorage) return;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
};

export const readConsent = (): CookieConsentState | null => {
  if (isWidget) return widgetConsent;

  const fromCookie = parseConsent(readCookie());
  if (fromCookie) return fromCookie;

  const fromLocal = parseConsent(readLocalStorage());
  if (fromLocal) {
    writeCookie(JSON.stringify(fromLocal));
    return fromLocal;
  }

  return null;
};

export const CONSENT_CHANGE_EVENT = 'squire:cookie-consent-change';

const dispatchChange = (state: CookieConsentState): void => {
  if (
    !hasWindow() ||
    typeof window.dispatchEvent !== 'function' ||
    typeof CustomEvent !== 'function'
  ) {
    return;
  }
  window.dispatchEvent(
    new CustomEvent<CookieConsentState>(CONSENT_CHANGE_EVENT, {
      detail: state,
    }),
  );
};

export const writeConsent = (
  state: Omit<CookieConsentState, 'version' | 'date'> &
    Partial<Pick<CookieConsentState, 'version' | 'date'>>,
): CookieConsentState => {
  const full: CookieConsentState = {
    necessary: state.necessary,
    statistics: state.statistics,
    version: state.version ?? CONSENT_VERSION,
    date: state.date ?? new Date().toISOString(),
  };
  if (isWidget) {
    widgetConsent = full;
    pushConsentToParent(full);
  } else {
    const serialized = JSON.stringify(full);
    writeCookie(serialized);
    writeLocalStorage(serialized);
  }
  dispatchChange(full);
  return full;
};

export const loadConsent = (): Promise<CookieConsentState | null> => {
  if (!isWidget) return Promise.resolve(readConsent());
  if (!widgetLoad) {
    widgetLoad = requestParentConsent()
      .then((raw) => {
        widgetConsent = parseConsent(raw);
        if (widgetConsent) dispatchChange(widgetConsent);
        return widgetConsent;
      })
      .finally(() => {
        widgetLoad = null;
      });
  }
  return widgetLoad;
};

export const clearConsent = (): void => {
  if (isWidget) {
    widgetConsent = null;
    return;
  }
  deleteCookie();
  removeLocalStorage();
};
